import {BaseComponent} from './base-component.js'
import {customElement, property, state, query} from 'lit/decorators.js'
import {html, css, CSSResultGroup, TemplateResult} from 'lit'
import {unsafeHTML} from 'lit/directives/unsafe-html.js'

@customElement('il-radio')
export default class RadioComponent extends BaseComponent {
  @property({type: Boolean}) disabled?: boolean
  @property({type: String}) label?: string
  @property({type: String}) hint?: string
  @property({type: Boolean, attribute: 'truncate-hint'}) truncateHint: boolean = false
  @property({type: String}) name?: string
  @property({type: String}) value?: string | number | object
  @property({type: Boolean}) checked?: boolean
  @property({type: Boolean}) selected: boolean = false
  @property({type: String, attribute: 'extra-content'}) extraContent?: string
  @property({type: Function})
  onClick?: () => void

  @query('.il-radio__hint') hintElement?: HTMLElement
  @state() isHintClamped: boolean = false
  @state() isHintExpanded: boolean = false

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-radio-container {
        cursor: pointer;
        display: flex;
        margin: .375rem 0;
      }

      .il-radio-container.il-radio-container--disabled {
        opacity: 0.5;
      }

      .il-radio__label {
        display: flex;
        flex-grow: 1;
        align-items: center;
        margin-left: .75rem;
        cursor: pointer;
        color: var(--il-color-neutrals-300);
      }

      .il-radio__label > p {
        font-size: var(--il-font-size-16);
        margin-top: 0;
        margin-bottom: 0;
      }

      .il-radio__hint-wrapper {
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-12);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
        position: relative;
        margin-top: 4px;
        margin-left: .75rem;
      }

      .il-radio__hint-wrapper.il-radio__hint-wrapper--clickable {
        cursor: pointer;
      }

      .il-radio__hint-wrapper.il-radio__hint-wrapper--clickable:hover {
        color: var(--il-color-neutrals-400);
      }

      .il-radio__hint {
        display: inline;
      }

      .il-radio__hint.il-radio__hint--truncate {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        padding-right: 24px;
      }

      .il-radio__hint.il-radio__hint--expanded {
        display: block;
        -webkit-line-clamp: unset;
      }

      .il-radio__hint > p {
        margin-top: 0;
        margin-bottom: 0;
      }

      .il-radio__hint-toggle {
        background: none;
        border: none;
        color: var(--il-color-accent-300);
        cursor: pointer;
        padding: 0;
        display: inline-flex;
        align-items: center;
        position: absolute;
        bottom: 0;
        right: 0;
        padding-left: 8px;
        pointer-events: none;
      }

      .il-radio__hint-wrapper--clickable:hover .il-radio__hint-toggle {
        color: var(--il-color-accent-400);
      }

      .il-radio-content {
        flex-grow: 1;
      }

      .il-radio__extra-content-wrapper {
        display: inline-flex;
        margin-left: .75rem;
        padding: 1px 6px;
        border: 2px solid var(--il-color-accent-100);
        border-radius: 4px;
        background-color: var(--il-color-accent-50);
        color: var(--il-color-accent-400);
        margin-top: .25rem;
      }

      .il-radio__extra-content-wrapper span {
        padding-left: 4px;
        font-weight: normal;
      }

      .il-radio__expand-icon {
        width: 20px;
        height: 20px;
        transition: transform 0.2s ease;
      }

      .il-radio__expand-icon--expanded {
        transform: rotate(180deg);
      }

      .il-radio__input {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        place-content: center;
        block-size: 100%;
        box-sizing: border-box;
        cursor: pointer;
        font-family: inherit;
        font-size: 100%;
        margin: 0;
        padding: 0;
        overflow: visible;
        appearance: none;
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 50%;
        transform: translateY(-0.075rem);
        background-color: var(--il-color-neutrals-50);
        border: 1px solid var(--il-color-neutrals-200);
      }

      .il-radio__input:checked {
        border: 1px solid var(--il-color-accent-300);
      }

      il-radio__input:not(:checked) il-radio__input {
        border: 1px solid red;
      }

      .il-radio__input::before {
        content: '';
        width: 0.75rem;
        height: 0.75rem;
        border-radius: 50%;
        transform: scale(0);
        transition: .2s transform ease-in-out;
        box-shadow: inset 1rem 1rem var(--il-color-accent-300);
      }

      .il-radio__input:checked::before {
        transform: scale(1);
        border-color: var(--il-color-accent-300);
      }

      .il-radio-container:hover:not(.il-radio-container--disabled) .il-radio__input {
        border-color: var(--il-color-neutrals-250);
      }

      .il-radio-container:hover:not(.il-radio-container--disabled) .il-radio__label {
        color: var(--il-color-neutrals-400);
      }

      .il-radio-container:hover:not(.il-radio-container--disabled) .il-radio__input:checked {
        border-color: var(--il-color-accent-400);
      }

      .il-radio-container:hover:not(.il-radio-container--disabled) .il-radio__input::before {
        box-shadow: inset 1rem 1rem var(--il-color-accent-400);
      }

      .il-radio__input:focus-visible {
        outline: none;
        border-color: var(--il-color-accent-200);
      }
    `,
  ] as CSSResultGroup

  /**
   * returns an internal id to be used on the input and label
   * gets the @id property passed from the glimmer component and adds so it is a unique ID
   *
   * if this component is used without glimmer, this should also work by passing the @id property
   * when calling the web component HTML tag, e.g.:
   * <iliad-radio id="some-id"></iliad-radio>
   */
  get internalId (): string {
    return `iliad-${this.id}`
  }

  override updated(changedProperties: Map<string, any>): void {
    super.updated(changedProperties)

    if (this.truncateHint && this.hintElement && (changedProperties.has('hint') || changedProperties.has('truncateHint'))) {
      this.checkIfHintIsClamped()
    }
  }

  checkIfHintIsClamped(): void {
    if (this.hintElement) {
      this.isHintClamped = this.hintElement.scrollHeight > this.hintElement.clientHeight
    }
  }

  toggleHintExpansion(): void {
    this.isHintExpanded = !this.isHintExpanded
  }

  override render (): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-radio-container ${this.disabled
          ? 'il-radio-container--disabled'
          : ''}"
        data-test-iliad-radio-container
      >
        <input
          id=${this.internalId}
          type="radio"
          name=${this.name}
          value=${this.value}
          selected=${this.selected}
          ?checked=${this.checked}
          ?disabled=${this.disabled}
          @click="${() => this._onClick()}"
          class="il-radio__input"
          data-test-iliad-radio
        />

        <div class="il-radio-content">
          <label
            for=${this.internalId}
            class="il-radio__label"
            data-test-iliad-radio-label
          >
            ${unsafeHTML(this.label)}
          </label>

          ${this.extraContent ? html`
            <div class="il-radio__extra-content-wrapper il-font-component-bold-16" data-test-iliad-radio-extra-content>
              ${unsafeHTML(this.extraContent)}
            </div>
          ` : ''}

          <div
            class="il-radio__hint-wrapper ${this.isHintClamped ? 'il-radio__hint-wrapper--clickable' : ''}"
            @click="${this.isHintClamped ? this.toggleHintExpansion : null}"
          >
            <div class="il-radio__hint ${this.truncateHint && !this.isHintExpanded ? 'il-radio__hint--truncate' : ''} ${this.isHintExpanded ? 'il-radio__hint--expanded' : ''}" data-test-iliad-radio-hint="${this.id}">
              ${unsafeHTML(this.hint)}
            </div>

            ${this.isHintClamped ? html`
              <button
                class="il-radio__hint-toggle"
                data-test-iliad-radio-hint-toggle
              >
                <svg
                  class="il-radio__expand-icon ${this.isHintExpanded ? 'il-radio__expand-icon--expanded' : ''}"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    d="M10 13.0625L5 8.0625L6.0625 7L10 10.9375L13.9375 7L15 8.0625L10 13.0625Z"
                  />
                </svg>
              </button>
            ` : ''}
          </div>
        </div>
      </div>
    `
  }

  _onClick (): void {
    this.onClick?.()
  }
}
